import type { IRuleMetadataCheck } from '@atlas/types'
import { FIELD_LABEL_CLASS } from '@atlas/ui-components'
import { Combobox, Label } from '@atlas/ui-primitives'
import { facetsToRailAxes } from '@/components/classification/ClassifyPicker'
import type { Facet } from '@/domain/library.types'
import {
	CLASSIFICATION_CHECK_PREFIX,
	RULE_METADATA_OPERATOR_ORDER,
	RuleMetadataOperator,
	RuleValidationMethod,
} from '@/domain/rules.types'
import { sortedByLabel } from '@/lib/option-order'

export interface RuleValidationValue {
	validationMethod: RuleValidationMethod
	metadataCheck: IRuleMetadataCheck | null
}

export interface RuleValidationFieldsProps extends RuleValidationValue {
	facets: Facet[]
	/** Namespaces the control ids so create and edit can both render on one page. */
	idPrefix: string
	onChange: (next: RuleValidationValue) => void
}

const fieldLabel = `mb-1.5 block ${FIELD_LABEL_CLASS}`
const selectCls = 'h-9 w-full rounded-ds-md border border-ds-border bg-ds-background px-2 text-sm text-ds-text'

/**
 * How a rule is judged, shared by the create and edit forms: the method, and for
 * `metadata` the deterministic check — an axis to look up in the subject's
 * classification plus the present/absent operator.
 *
 * Switching away from `metadata` clears the check, which the API requires: a
 * `metadataCheck` beside any other method is rejected.
 */
export function RuleValidationFields({
	validationMethod,
	metadataCheck,
	facets,
	idPrefix,
	onChange,
}: RuleValidationFieldsProps) {
	const isMetadata = validationMethod === RuleValidationMethod.METADATA
	const axisCode = metadataCheck?.field.startsWith(CLASSIFICATION_CHECK_PREFIX)
		? metadataCheck.field.slice(CLASSIFICATION_CHECK_PREFIX.length)
		: undefined

	function setValidationMethod(next: RuleValidationMethod) {
		onChange({
			validationMethod: next,
			metadataCheck: next === RuleValidationMethod.METADATA ? metadataCheck : null,
		})
	}

	function setCheckAxis(code: string | undefined) {
		onChange({
			validationMethod,
			metadataCheck: code
				? {
						field: `${CLASSIFICATION_CHECK_PREFIX}${code}`,
						operator: metadataCheck?.operator ?? RuleMetadataOperator.PRESENT,
					}
				: null,
		})
	}

	function setCheckOperator(operator: RuleMetadataOperator) {
		if (!metadataCheck) return
		onChange({ validationMethod, metadataCheck: { ...metadataCheck, operator } })
	}

	return (
		<>
			<div>
				<Label htmlFor={`${idPrefix}-validation-method`} className={fieldLabel}>
					Validation method
				</Label>
				<select
					id={`${idPrefix}-validation-method`}
					value={validationMethod}
					onChange={(e) => setValidationMethod(e.target.value as RuleValidationMethod)}
					className={selectCls}
				>
					<option value={RuleValidationMethod.METADATA}>metadata</option>
					<option value={RuleValidationMethod.MULTIMODAL_LLM}>multimodal-LLM</option>
				</select>
			</div>

			{isMetadata && (
				<div>
					<span className={fieldLabel}>Metadata check</span>
					<div className="flex flex-wrap items-end gap-2">
						<div className="w-48">
							<Combobox
								aria-label="Classification to check"
								options={sortedByLabel(facetsToRailAxes(facets).map((a) => ({ value: a.code, label: a.label })))}
								value={axisCode ?? null}
								onChange={setCheckAxis}
								placeholder="Classification…"
							/>
						</div>
						<select
							aria-label="Operator"
							value={metadataCheck?.operator ?? RuleMetadataOperator.PRESENT}
							onChange={(e) => setCheckOperator(e.target.value as RuleMetadataOperator)}
							disabled={!axisCode}
							className="h-9 w-40 rounded-ds-md border border-ds-border bg-ds-background px-2 text-sm text-ds-text disabled:opacity-60"
						>
							{RULE_METADATA_OPERATOR_ORDER.map((op) => (
								<option key={op} value={op}>
									{op}
								</option>
							))}
						</select>
					</div>
				</div>
			)}
		</>
	)
}
